import { useState, useEffect, useMemo } from 'react';
import { staffAPI } from '../services/api';

export default function useDashboardMetrics(analyticsSetters) {
  const [stats, setStats] = useState({
    totalEmployees: 0, activeProjects: 0, todayAttendance: 0,
    pendingLeave: 0, clockedIn: 0, departments: 0, pendingDocuments: 0,
  });
  const [loading, setLoading] = useState(true);
  const [analyticsStatus, setAnalyticsStatus] = useState({
    loaded: false, errors: [], successCount: 0, totalCount: 13,
  });
  const [employmentPassData, setEmploymentPassData] = useState(null);
  const [showWorkforceModal, setShowWorkforceModal] = useState(false);

  // Workforce planning state populated by analytics fetch
  const [workforceAgeProfile, setWorkforceAgeProfile] = useState(null);
  const [retirementRisk, setRetirementRisk] = useState(null);
  const [newHireRetention, setNewHireRetention] = useState(null);
  const [staffWorkerRatio, setStaffWorkerRatio] = useState(null);
  const [workforceMetricsTrends, setWorkforceMetricsTrends] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const dashboardMetricsResponse = await staffAPI.getDashboardMetrics();
        const metrics = dashboardMetricsResponse.data;
        setStats({
          totalEmployees: metrics.total_employees || 0,
          activeProjects: metrics.active_projects || 0,
          todayAttendance: metrics.today_attendance || 0,
          pendingLeave: metrics.pending_leave_requests || 0,
          clockedIn: metrics.clocked_in_today || 0,
          departments: metrics.departments_count || 0,
          pendingDocuments: metrics.pending_documents || 0,
          attendancePercentage: metrics.attendance_percentage || 0,
          attendanceYesterdayComparison: metrics.attendance_yesterday_comparison || null,
          leaveApprovalRate: metrics.leave_approval_rate ?? null,
          projectsCompleted: metrics.projects_completed || 0,
          projectsOverdue: metrics.projects_overdue || 0,
          documentsVerifiedToday: metrics.documents_verified_today || 0,
          verificationRate: metrics.verification_rate || 0,
          approvedLeaveToday: metrics.approved_leave_today || 0,
          lastUpdated: metrics.last_updated,
          dataFreshness: metrics.data_freshness,
        });

        try {
          const analyticsPromises = [
            staffAPI.getAgeGroupStats(),
            staffAPI.getClassificationAgeStats(),
            staffAPI.getNationalityStats(),
            staffAPI.getRaceDistributionStats(),
            staffAPI.getTenureDistributionStats(),
            staffAPI.getGenderDistributionStats(),
            staffAPI.getDepartmentAnalyticsStats(),
            staffAPI.getEmploymentPassBreakdownStats(),
            staffAPI.getWorkforceAgeProfile(),
            staffAPI.getRetirementRisk(),
            staffAPI.getNewHireRetention(),
            staffAPI.getStaffWorkerRatio(),
            staffAPI.getWorkforceMetricsTrends(),
          ];

          const totalAnalyticsCount = analyticsPromises.length;
          const analyticsResults = await Promise.allSettled(analyticsPromises);

          const [
            ageGroupResult, classificationAgeResult, nationalityResult,
            raceResult, tenureResult, genderResult, departmentResult,
            employmentPassResult, workforceAgeProfileResult, retirementRiskResult,
            newHireRetentionResult, staffWorkerRatioResult, workforceMetricsTrendsResult,
          ] = analyticsResults;

          const extractData = (result, fallback = []) => {
            if (result.status === 'fulfilled' && result.value?.data) return result.value.data;
            if (result.status === 'rejected') {
              console.warn('Analytics API failed:', result.reason?.response?.config?.url || 'unknown', result.reason?.message);
            }
            return fallback;
          };

          // Populate analytics via setters from useAnalyticsData
          if (analyticsSetters) {
            analyticsSetters.setAgeGroupData(extractData(ageGroupResult));
            analyticsSetters.setClassificationAgeData(extractData(classificationAgeResult));
            analyticsSetters.setNationalityData(extractData(nationalityResult));
            analyticsSetters.setRaceData(extractData(raceResult));
            const tenureResponse = extractData(tenureResult, {});
            analyticsSetters.setTenureData(tenureResponse.buckets || tenureResponse);
            if (analyticsSetters.setAverageTenure && tenureResponse.average_tenure_years != null) {
              analyticsSetters.setAverageTenure(tenureResponse.average_tenure_years);
            }
            analyticsSetters.setGenderData(extractData(genderResult));
            analyticsSetters.setDepartmentData(extractData(departmentResult));
          }

          setEmploymentPassData(extractData(employmentPassResult, null));
          setWorkforceAgeProfile(extractData(workforceAgeProfileResult, null));
          setRetirementRisk(extractData(retirementRiskResult, null));
          setNewHireRetention(extractData(newHireRetentionResult, null));
          setStaffWorkerRatio(extractData(staffWorkerRatioResult, null));
          setWorkforceMetricsTrends(extractData(workforceMetricsTrendsResult, []));

          const successfulCalls = analyticsResults.filter(r => r.status === 'fulfilled').length;
          const failedEndpoints = analyticsResults
            .map((result, index) => {
              const endpoints = ['age_group_stats', 'classification_age_stats', 'nationality_stats', 'race_distribution_stats', 'tenure_distribution_stats', 'gender_distribution_stats', 'department_analytics_stats'];
              return result.status === 'rejected' ? endpoints[index] : null;
            })
            .filter(Boolean);

          setAnalyticsStatus({
            loaded: true, errors: failedEndpoints,
            successCount: successfulCalls, totalCount: totalAnalyticsCount,
          });
        } catch (analyticsError) {
          console.warn('⚠️ Analytics data failed to load:', analyticsError);
          if (analyticsSetters) {
            analyticsSetters.setAgeGroupData([]);
            analyticsSetters.setClassificationAgeData([]);
            analyticsSetters.setNationalityData([]);
            analyticsSetters.setRaceData([]);
            analyticsSetters.setTenureData([]);
            analyticsSetters.setGenderData([]);
            analyticsSetters.setDepartmentData([]);
          }
          setAnalyticsStatus({
            loaded: true,
            errors: ['age_group_stats', 'classification_age_stats', 'nationality_stats', 'race_distribution_stats', 'tenure_distribution_stats', 'gender_distribution_stats', 'department_analytics_stats'],
            successCount: 0, totalCount: 7,
          });
        }
      } catch (error) {
        console.error('❌ Error fetching dashboard metrics:', error);
        setStats({
          totalEmployees: 0, activeProjects: 0, todayAttendance: 0,
          pendingLeave: 0, clockedIn: 0, departments: 0, pendingDocuments: 0,
          attendancePercentage: 0, leaveApprovalRate: null,
          projectsCompleted: 0, projectsOverdue: 0,
          documentsVerifiedToday: 0, verificationRate: 0,
          approvedLeaveToday: 0, dataFreshness: 'error',
        });
        if (analyticsSetters) {
          analyticsSetters.setAgeGroupData([]);
          analyticsSetters.setClassificationAgeData([]);
          analyticsSetters.setNationalityData([]);
          analyticsSetters.setRaceData([]);
          analyticsSetters.setTenureData([]);
          analyticsSetters.setGenderData([]);
          analyticsSetters.setDepartmentData([]);
        }
        setAnalyticsStatus({
          loaded: true,
          errors: ['age_group_stats', 'classification_age_stats', 'nationality_stats', 'race_distribution_stats', 'tenure_distribution_stats', 'gender_distribution_stats', 'department_analytics_stats'],
          successCount: 0, totalCount: 7,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  // analyticsSetters is stable (object from useAnalyticsData doesn't change identity with useMemo/useRef)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    stats, loading, analyticsStatus,
    employmentPassData, showWorkforceModal, setShowWorkforceModal,
    workforceAgeProfile, retirementRisk, newHireRetention,
    staffWorkerRatio, workforceMetricsTrends,
  };
}
